/*
 * MATERIAL.JSX - Material Management Component for Add Task
 *
 * SUMMARY:
 * Implemented item overlay functionality for both Item Description and Part No fields,
 * replacing traditional Autocomplete dropdowns with a powerful multi-select overlay.
 *
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import Table from "react-bootstrap/Table";
import "../scss/task.scss";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import idx from "idx";
import { itemFields } from "../../../constants/fields.constant";
import { updateItemAction, updateSubTaskAction } from "../../../slice/add-task";
import { useSelector, useDispatch } from "react-redux";
import _, { isBoolean, debounce } from "lodash";
import { getInventoryLists } from "../../../actions/inventory.action";
import { calculateSaleAmount } from "../../../util/calculations/sale-amount";
import { warningSnackBar } from "../../../actions/common.action";
import {
  debounceTime,
  filterItems,
  filterManufacturer,
  isInlineItem,
  INLINE_ITEM_IDENTIFIER,
  INLINE_CATALOG_DISPLAY_NAME,
  INLINE_CATEGORY_DISPLAY_NAME,
} from "../../../util/common";
import { getFormatedUnit } from "../../../util";
import { v4 as uuidv4 } from "uuid";
import ProfitMarginLock from "../../../components/profitMarginLock-dialog";
import CustomInput, {
  LockInput,
  NumberInput,
} from "../../../components/inputs/new-input";
import AddItemDialog from "../add-item-dialog";
import SelectInput from "../../../components/inputs/select";
import { toPercent, toUSD } from "../../../util/currency-formatter";
import { toDecimal } from "../../../util/decimal-utils";
import ItemSelectionOverlay from "../../../components/item-selection-overlay/item-selection-overlay";
import { Tooltip } from "@mui/material";

const filter = createFilterOptions({
  ignoreCase: true,
  matchFrom: "any",
});

export default function Material({
  material = [],
  newMaterial = [],
  total = {},
  profitMargin,
  profitMarginType,
  isDisabled,
  isVariation = false,
  handleAddMaterial,
  categoryList = [],
  unitList = [],
  ...props
}) {
  const dispatch = useDispatch();
  const permissions =
    useSelector((state) => state.user.userData.permissions) || {};
  const wCatalogPermission =
    idx(permissions, (_) => _.catalog) === 2 ? true : false;

  // Create input refs object for storing input element references
  const inputRefs = useRef({});
  const searchTimerRef = useRef(null);

  const [materialState, setMaterialState] = useState({
    itemsList: [],
    items: [],
    filterValue: {},
    item: itemFields,
    profitMarginDialogState: false,
    lockArrayValue: null,
    confirmLock: false,
    addItemDialogState: false,
    // Item overlay related state
    overlayOpen: false,
    overlayAnchorEl: null,
    overlayItems: [],
    selectedItemIds: [],
    isLoadingItems: false,
    currentOverlayTmpId: null,
    currentSearchQuery: "",
    isPartNoSearch: false,
  });

  useEffect(() => {
    initLoad();

    // Cleanup function to clear timer on unmount
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);


  // Watch for unitList prop changes and update state
  useEffect(() => {
    const loadUnits = async () => {
      let units = [];

      // If unitList is a function (async getUnits from subtask.jsx), call it
      if (typeof unitList === 'function') {
        try {
          units = await unitList();
        } catch (error) {
          console.error('Error loading units:', error);
          units = [];
        }
      } else if (unitList && Array.isArray(unitList) && unitList.length > 0 && unitList[0]?.units) {
        // If unitList is raw data array, format it
        const allUnits = unitList[0].units || [];
        units = getFormatedUnit(allUnits);
      }

      if (units && units.length > 0) {
        setMaterialState((prev) => ({
          ...prev,
          item: {
            ...prev.item,
            unit: {
              ...prev.item.unit,
              items: units,
            },
          },
        }));
      }
    };

    loadUnits();
  }, [unitList]);

  const initLoad = () => {
    debouncedFetchItems();
    const allUnits =
      (unitList &&
        Array.isArray(unitList) &&
        unitList.length > 0 &&
        unitList[0].units) ||
      [];
    const units = getFormatedUnit(allUnits);
    let { item } = materialState;
    item.unit.items = units;
    setMaterialState((prev) => ({
      ...prev,
      item,
    }));
  };

  const fetchItems = (filter) => {
    let categoryFilter = filter && filter.catalog ? filter.catalog : "";
    let categoryId = filter && filter.catalogid ? filter.catalogid : "";
    let manufacturerFilter =
      filter && filter.manufacturer ? filter.manufacturer : "";
    let search = filter && filter.item ? filter.item : "";
    let partNoFilter = filter && filter.partNo ? filter.partNo : "";
    let params = {};

    params = {
      ...(categoryId ? { categoryId } : { categoryFilter }),
      manufacturerFilter,
      search,
      partNoFilter,
    };

    if (params.categoryId || params.search || params.partNoFilter) {
      dispatch(getInventoryLists(params)).then((items) => {
        const category = items.category;
        setMaterialState((prev) => {
          const updatedFilterValue = { ...prev.filterValue };
          for (const tmpId in updatedFilterValue) {
            updatedFilterValue[tmpId] = {
              ...updatedFilterValue[tmpId],
              itemOptions: filterItems(category, updatedFilterValue[tmpId]),
              partNoOptions: partNoDropDown({ category }),
            };
          }
          return {
            ...prev,
            itemsList: category,
            filterValue: updatedFilterValue,
          };
        });
      });
    }
  };

  const debouncedFetchItems = useCallback(
    debounce((filter) => {
      fetchItems(filter);
    }, debounceTime.inventoryList),
    []
  );

  const getCategoriesList = () => {
    return categoryList.map((category) => ({
      key: category.name,
      name: category.name,
      value: category.id,
    }));
  };

  const profitMarginDialogHandler = (inputValue, index) => {
    setMaterialState((prev) => ({
      ...prev,
      lockArrayValue: index,
      profitMarginDialogState: !prev.profitMarginDialogState,
    }));
  };

  const itemChangeHandler = (e, index) => {
    let _temp = _.cloneDeep(material);
    switch (e) {
      case "saleAmountLock":
        profitMarginDialogHandler(e, index);
        if (
          materialState.profitMarginDialogState &&
          materialState.confirmLock
        ) {
          _temp[index]["saleAmountLock"].value =
            !_temp[index]["saleAmountLock"].value;
        }
        break;
      default:
        if (e.target.name === "costPerItem" || e.target.name === "qty") {
          // Only block negative values if NOT a variation
          if (!isVariation && e.target.value < 0) break;
        }

        _temp[index][e.target.name].value = e.target.value;

        const is_locked = _temp[index]["saleAmountLock"].value || false;
        let saleAmount = _temp[index]["saleAmount"].value;

        if (e.target.name === "saleAmountLock") {
          _temp[index]["saleAmountLock"].value =
            !_temp[index]["saleAmountLock"].value;
        }

        if (e.target.name === "costPerItem" || e.target.name === "qty") {
          const isLocked = _temp[index]["saleAmountLock"].value || false;
          if (isLocked || _temp[index]["saleAmount"].value <= 0) {
            _temp[index]["saleAmount"].value = calculateSaleAmount({
              cost: _temp[index]["costPerItem"].value,
              profitMargin,
              profitMarginType,
            });
          }
        }
        const qty = toDecimal(_temp[index]["qty"].value || 0);
        const sale = toDecimal(_temp[index]["saleAmount"].value || 0);
        _temp[index]["total"].value = qty.times(sale).toNumber();
    }

    updateMaterials(_temp);
  };

  const removeItem = (index) => {
    let _temp = _.cloneDeep(material);
    _temp.splice(index, 1);
    updateMaterials(_temp);
  };

  const updateMaterials = (materials, newMaterials = null) => {
    if (!Array.isArray(materials)) return;
    const { subTaskArray, subTaskArrayIndex } = props;
    let _temp = _.cloneDeep(subTaskArray);
    _temp[subTaskArrayIndex]["material"] = materials;
    if (newMaterials) _temp[subTaskArrayIndex]["newMaterial"] = newMaterials;
    _temp[subTaskArrayIndex]["isedited"] = true;
    dispatch(updateSubTaskAction(_temp));
  };

  const filterItem = (id) => {
    const item = (materialState.itemsList || []).find((item) => item.id === id);
    return item;
  };

  const itemsDropDownOption = (tmpId) => {
    let filters = idx(materialState, (_) => _.filterValue[tmpId]) || {};
    let itemsList = _.cloneDeep(materialState.itemsList);
    let filterOptions = filterItems(itemsList, filters);
    return filterOptions;
  };

  const addFilter = (value, type, tmpId, skipFetchItem = false) => {
    let filters = idx(materialState, (_) => _.filterValue[tmpId]) || {};
    let showMaterialLockIcon = getLockStatus(tmpId);
    if (!filters) filters = {};
    if (filters && filters[type] !== value) {
      filters[type] = value;
      if (type === "saleAmount" && !showMaterialLockIcon) {
        filters["saleAmount"] = value;
        // filters["saleAmountLock"] = !filters["saleAmountLock"];
      }
      if (type === "costPerItem" || type === "qty" || type === "saleAmount") {
        if (showMaterialLockIcon)
          filters["saleAmount"] = calculateSaleAmount({
            cost: parseFloat(filters["costPerItem"]),
            profitMargin,
            profitMarginType,
          });

        const qty = toDecimal(filters["qty"] || 0);
        const costPerItem = toDecimal(filters["costPerItem"] || 0);
        const saleAmount = toDecimal(filters["saleAmount"] || 0);
        const totalValue = qty.times(saleAmount);
        filters["totalValue"] = totalValue.toNumber();
      }
      let filterValue = materialState.filterValue;
      filterValue[tmpId] = {
        ...filters,
        showMaterialLockIcon,
        manufacturerOptions: manufacturerDropDown(tmpId),
        itemOptions: itemsDropDownOption(tmpId),
        partNoOptions: partNoDropDown({}),
      };
      setMaterialState((prev) => ({
        ...prev,
        filterValue: filterValue,
      }));
      const fetchTriggers = ["catalog", "catalogid", "manufacturer", "item", "partNo"];
      !skipFetchItem && fetchTriggers.includes(type) && debouncedFetchItems(filterValue[tmpId]);
    }
  };

  const lockMaterialIconChange = (tmpId) => {
    let filters = idx(materialState, (_) => _.filterValue[tmpId]) || {};
    let filterValue = materialState.filterValue;
    let status = getLockStatus(tmpId);

    if (!status)
      filters["saleAmount"] = calculateSaleAmount({
        cost: parseFloat(filters["costPerItem"]),
        profitMargin,
        profitMarginType,
      });

    const qty = toDecimal(filters["qty"] || 0);
    const sale = toDecimal(filters["saleAmount"] || 0);
    const totalValue = qty.times(sale);
    filters["totalValue"] = totalValue.toNumber();

    filterValue[tmpId] = { ...filters, showMaterialLockIcon: !status };
    setMaterialState((prev) => ({
      ...prev,
      filterValue: filterValue,
    }));
  };

  const manufacturerDropDown = (tmpId) => {
    let filters = idx(materialState, (_) => _.filterValue[tmpId]) || {};
    let itemsList = _.cloneDeep(materialState.itemsList);
    let filterOptions = filterManufacturer(itemsList, filters);
    return filterOptions;
  };

  const setConfirmed = () => {
    const { lockArrayValue } = materialState;
    let _selectedItemsList = _.cloneDeep(material);
    _selectedItemsList[lockArrayValue]["saleAmountLock"].value =
      !_selectedItemsList[lockArrayValue]["saleAmountLock"].value;
    _selectedItemsList.forEach((item, index) => {
      if (item["saleAmountLock"].value || item["saleAmount"].value <= 0) {
        _selectedItemsList[index]["saleAmount"].value = calculateSaleAmount({
          cost: item["costPerItem"].value,
          profitMargin,
          profitMarginType,
        });
      }
      const qty = toDecimal(item["qty"].value || 0);
      const sale = toDecimal(_selectedItemsList[index]["saleAmount"].value || 0);
      _selectedItemsList[index]["total"].value = qty.times(sale).toNumber();
    });
    setMaterialState((prev) => ({
      ...prev,
      profitMarginDialogState: !prev.profitMarginDialogState,
    }));
    updateMaterials(_selectedItemsList);
  };

  const AddItemDialogHandler = (tmpId) => {
    setMaterialState((prev) => ({
      ...prev,
      addItemDialogState: !prev.addItemDialogState,
      tmpId,
    }));
  };

  const AddItemDialogClose = (res = {}) => {
    if (!_.isEmpty(res)) {
      debouncedFetchItems();
      addItemFromResponse(res);
    }
    setMaterialState((prev) => ({ ...prev, addItemDialogState: false }));
  };

  const addItemFromResponse = (res) => {
    let selectedMaterials = _.cloneDeep(material);

    const item = JSON.parse(JSON.stringify(materialState.item));
    item.name.value = res.item;
    item.taskId.value = null;
    item.id = res.id;
    item.manufacturer = res.manufacturer;
    item.partNo = res.part_no;
    item.categoryId = res.category_id;
    item.costPerItem.value = res.cost;
    item.saleAmount.value =
      (res.sale_amount_lock
        ? calculateSaleAmount({
          cost: res.cost,
          profitMargin,
          profitMarginType,
        })
        : res.sale_amount || res.cost) || 0;
    item.saleAmountLock.value = res.sale_amount_lock;
    item.unit.value = res.unit || null;
    item.qty.value = 1;
    const qty = toDecimal(item.qty.value || 0);
    const sale = toDecimal(item.saleAmount.value || 0);
    item.total.value = qty.times(sale).toNumber();

    selectedMaterials.push(item);
    let materials = getNewMaterials();
    materials = materials.filter((item) => item.id != materialState.tmpId);
    updateMaterials(selectedMaterials, materials);
  };

  const getNewMaterials = () => {
    const { subTaskArray, subTaskArrayIndex } = props;
    let _temp = _.cloneDeep(subTaskArray);
    return _temp[subTaskArrayIndex]["newMaterial"] || [];
  };
  const updateNewMaterials = (materials) => {
    const { subTaskArrayIndex } = props;
    dispatch(
      updateItemAction({ material: materials, index: subTaskArrayIndex })
    );
  };
  const handleRemoveMaterial = (tmpId) => {
    let materials = getNewMaterials();
    materials = materials.filter((item) => item.id != tmpId);
    updateNewMaterials(materials);
  };


  /**
   * Adds an inline item (custom item not from inventory catalog) to the material list
   * @param {string} tmpId - Temporary ID of the material row
   */
  const addInlineItem = (tmpId) => {
    const filters = idx(materialState, (_) => _.filterValue[tmpId]) || {};

    // Validate required fields
    if (!filters.item || filters.item.trim() === "") {
      dispatch(warningSnackBar("Please enter an Item Name"));
      return;
    }
    if (!filters.partNo || filters.partNo.trim() === "") {
      dispatch(warningSnackBar("Please enter a Part No"));
      return;
    }
    if (!filters.costPerItem || parseFloat(filters.costPerItem) <= 0) {
      dispatch(warningSnackBar("Please enter a valid Cost"));
      return;
    }
    if (!filters.qty || parseFloat(filters.qty) <= 0) {
      dispatch(warningSnackBar("Please enter a valid Quantity"));
      return;
    }
    if (!filters.unit || filters.unit.trim() === "") {
      dispatch(warningSnackBar("Please select a Unit"));
      return;
    }

    let selectedMaterials = _.cloneDeep(material);

    const item = JSON.parse(JSON.stringify(materialState.item));
    item.name.value = filters.item;
    item.id = uuidv4();
    item.manufacturer = INLINE_CATEGORY_DISPLAY_NAME;
    item.partNo = filters.partNo;
    item.categoryId = INLINE_ITEM_IDENTIFIER;
    item.categoryName = INLINE_CATEGORY_DISPLAY_NAME;
    item.catalog = INLINE_ITEM_IDENTIFIER;
    item.isInlineItem = true;
    item.costPerItem.value = parseFloat(filters.costPerItem) || 0;
    item.saleAmount.value = getLockStatus(tmpId)
      ? calculateSaleAmount({
        cost: parseFloat(filters.costPerItem),
        profitMargin,
        profitMarginType,
      })
      : parseFloat(filters.saleAmount) || parseFloat(filters.costPerItem) || 0;
    item.saleAmountLock.value = getLockStatus(tmpId);
    item.unit.value = filters.unit || "";
    item.qty.value = parseFloat(filters.qty) || 0;
    const qty = toDecimal(item.qty.value || 0);
    const sale = toDecimal(item.saleAmount.value || 0);
    item.total.value = qty.times(sale).toNumber();

    selectedMaterials.push(item);

    // Remove the temporary row
    let materials = getNewMaterials();
    materials = materials.filter((m) => m.id !== tmpId);

    updateMaterials(selectedMaterials, materials);
  };

  const getLockStatus = (tmpId) => {
    return isBoolean(
      idx(materialState, (_) => _.filterValue[tmpId].showMaterialLockIcon)
    )
      ? idx(materialState, (_) => _.filterValue[tmpId].showMaterialLockIcon)
      : true;
  };

  const partNoDropDown = ({ category = [] }) => {
    let itemsList =
      category.length > 0 ? category : _.cloneDeep(materialState.itemsList);
    let filterOptions = itemsList.map((item) => ({
      key: item.partNo || item.part_no,
      value: item.partNo || item.part_no,
    }));

    filterOptions = _.uniqBy(filterOptions, "key");

    const sortedFilterOptions = _.sortBy(filterOptions, "key");

    return sortedFilterOptions;
  };

  // Item overlay methods
  /**
   * Opens the item selection overlay for searching and selecting inventory items
   * @Author Arutprakasam A
   * @Created 2024-01-20
   * @Modified 2024-01-20 - Added Part No search functionality support
   *
   * @description Opens a modal overlay that allows users to search and select multiple items
   * from inventory. Supports both item description search and part number search.
   *
   * @param {string} tmpId - Temporary ID of the material row
   * @param {HTMLElement} anchorEl - DOM element to anchor the overlay to
   * @param {string} searchQuery - Initial search query to filter items
   * @param {boolean} isPartNo - Whether this is a part number search (default: false)
   *
   * @returns {void}
   *
   * @example
   * // Open overlay for item description search
   * openItemOverlay('material-123', inputElement, 'heat exchanger', false);
   *
   * // Open overlay for part number search
   * openItemOverlay('material-123', inputElement, 'RHE09', true);
   */
  const openItemOverlay = (
    tmpId,
    anchorEl,
    searchQuery = "",
    isPartNo = false
  ) => {
    if (!anchorEl) {
      console.error("No anchor element provided for overlay");
      return;
    }

    setMaterialState((prev) => ({
      ...prev,
      overlayOpen: true,
      overlayAnchorEl: anchorEl,
      currentOverlayTmpId: tmpId,
      selectedItemIds: [],
      isLoadingItems: true,
      isPartNoSearch: isPartNo,
    }));

    // Immediately fetch items for overlay based on search query
    if (searchQuery && searchQuery.length > 0) {
      fetchItemsForOverlay(searchQuery, tmpId, isPartNo);
    } else {
      setMaterialState((prev) => ({ ...prev, isLoadingItems: false }));
    }
  };

  /**
   * Closes the item selection overlay and resets related state
   * @Author Arutprakasam A
   * @Created 2024-01-20
   *
   * @description Cleanly closes the overlay modal and resets all overlay-related state variables
   * to their initial values. Called when user clicks outside overlay, presses escape, or after
   * successful item addition.
   *
   * @returns {void} Resets overlay state to closed
   *
   * @example
   * // User clicks outside overlay or presses escape
   * closeItemOverlay();
   */
  const closeItemOverlay = () => {
    setMaterialState((prev) => ({
      ...prev,
      overlayOpen: false,
      overlayAnchorEl: null,
      overlayItems: [],
      selectedItemIds: [],
      currentOverlayTmpId: null,
      isLoadingItems: false,
      isPartNoSearch: false,
    }));
  };

  /**
   * Handles item selection/deselection in the overlay
   * @Author Arutprakasam A
   * @Created 2024-01-20
   *
   * @description Toggles item selection state in the overlay. Adds item to selection if not selected,
   * removes if already selected. Updates the selectedItemIds array which controls checkbox states
   * and enables/disables the "Add" button.
   *
   * @param {string} itemId - Unique identifier of the inventory item to toggle
   * @returns {void} Updates selectedItemIds state array
   *
   * @example
   * // User clicks checkbox for item with ID "12345"
   * handleItemSelection("12345");
   */
  const handleItemSelection = (itemId) => {
    setMaterialState((prev) => {
      const selectedIds = [...prev.selectedItemIds];
      const index = selectedIds.indexOf(itemId);

      if (index > -1) {
        selectedIds.splice(index, 1);
      } else {
        selectedIds.push(itemId);
      }

      return {
        ...prev,
        selectedItemIds: selectedIds,
      };
    });
  };

  /**
   * Adds selected items from overlay to the material list
   * @Author Arutprakasam A
   * @Created 2024-01-20
   * @Modified 2024-01-20 - Enhanced to properly handle multiple item selection and catalog names
   *
   * @description Processes all selected items from the overlay and adds them to the material list.
   * Includes duplicate checking, proper cost/sale amount calculations, and catalog name preservation.
   * Removes the temporary material row after successful addition.
   *
   * @returns {void} Updates material list state and closes overlay
   *
   * @dependencies
   * - materialState.selectedItemIds: Array of selected item IDs
   * - materialState.overlayItems: Available items in overlay
   * - materialState.currentOverlayTmpId: Current temporary row ID
   *
   * @example
   * // After user selects items and clicks "Add" button
   * addSelectedItemsToMaterial();
   *
   * @changes
   * - Modified to process all selected items in a single operation
   * - Added categoryName extraction and storage for proper catalog display
   * - Enhanced duplicate item checking with user feedback
   * - Improved temporary row removal logic (removes only once at end)
   * - Added profit margin calculation for sale amounts
   */
  const addSelectedItemsToMaterial = () => {
    const { selectedItemIds, overlayItems, currentOverlayTmpId } =
      materialState;

    if (!selectedItemIds.length || !currentOverlayTmpId) return;

    let selectedMaterials = _.cloneDeep(material);

    // Add all selected items to the material list
    selectedItemIds.forEach((itemId) => {
      const selectedItem = overlayItems.find(
        (item) => `${item.id}${item.cost}` === itemId || item.id === itemId
      );
      if (selectedItem) {
        // Check if item already exists
        const itemExist = selectedMaterials.find(
          (item) =>
            item.id === selectedItem.id &&
            item.categoryId === selectedItem.categoryId
        );

        if (!itemExist) {
          let filters =
            idx(materialState, (_) => _.filterValue[currentOverlayTmpId]) || {};

          // Get category information from the selected item
          const categoryId =
            selectedItem.categoryId ||
            (selectedItem.category && selectedItem.category.id);
          const categoryName =
            selectedItem.category && selectedItem.category.name;

          const item = JSON.parse(JSON.stringify(materialState.item));
          item.name.value = selectedItem.item;
          item.taskId.value = selectedItem.taskId || null;
          item.id = selectedItem.id || uuidv4();
          item.manufacturer = selectedItem.manufacturer;
          item.partNo = selectedItem.partNo || selectedItem.part_no;
          item.categoryId = categoryId;

          // Store category name for display purposes if available
          if (categoryName) {
            item.categoryName = categoryName;
          }

          item.costPerItem.value = filters.costPerItem || selectedItem.cost;
          const isLocked = getLockStatus(currentOverlayTmpId);
          item.saleAmount.value =
            !isLocked
              ? filters["saleAmount"] ||
              selectedItem.saleAmount ||
              selectedItem.cost
              : calculateSaleAmount({
                  cost: filters.costPerItem || selectedItem.cost,
                  profitMargin,
                  profitMarginType,
                }) || 0;
          item.saleAmountLock.value = isLocked;
          item.unit.value = selectedItem.unit || null;
          item.qty.value = 1;
          const qty = toDecimal(item.qty.value || 0);
          const sale = toDecimal(item.saleAmount.value || 0);
          item.total.value = qty.times(sale).toNumber();

          selectedMaterials.push(item);
        } else {
          dispatch(
            warningSnackBar(`Item "${selectedItem.item}" is Already Selected`)
          );
        }
      }
    });

    // Remove the temporary row only once at the end
    let materials = getNewMaterials();
    materials = materials.filter((item) => item.id != currentOverlayTmpId);

    // Update with all new materials
    updateMaterials(selectedMaterials, materials);

    closeItemOverlay();
  };

  /**
   * Fetches inventory items for the overlay based on search criteria
   *
   * @description Calls the getInventoryLists API to fetch inventory items based on search type.
   * For item description searches, uses 'search' parameter. For part number searches, uses 'partNoFilter' parameter.
   * Also applies catalog, manufacturer, and other filters from the current material row state.
   *
   * @param {string} searchQuery - The search term entered by user
   * @param {string|null} tmpId - Temporary ID of the material row for accessing filters
   * @param {boolean} isPartNo - Whether this is a part number search (affects API parameters)
   *
   * @returns {Promise<void>} Updates materialState with fetched items
   *
   * @example
   * // Fetch items by description
   * await fetchItemsForOverlay('heat exchanger', 'material-123', false);
   *
   * // Fetch items by part number
   * await fetchItemsForOverlay('RHE09-769', 'material-123', true);
   *
   * @changes
   * - Added isPartNo parameter to differentiate search types
   * - Modified API parameter logic: uses 'search' for descriptions, 'partNoFilter' for part numbers
   * - Enhanced parameter object construction for conditional API parameters
   */
  const fetchItemsForOverlay = async (
    searchQuery = "",
    tmpId = null,
    isPartNo = false
  ) => {
    if (!searchQuery || searchQuery.length < 1) {
      setMaterialState((prev) => ({
        ...prev,
        overlayItems: [],
        isLoadingItems: false,
      }));
      return;
    }

    const filters = idx(materialState, (_) => _.filterValue[tmpId]) || {};
    let categoryFilter = filters.catalog || "";
    let categoryId = filters.catalogid || "";
    let manufacturerFilter = filters.manufacturer || "";

    // Set search parameters based on search type
    let searchParam = "";
    let partNoFilter = "";

    if (isPartNo) {
      // When searching by Part No, use partNoFilter
      partNoFilter = searchQuery;
    } else {
      // When searching by Item Description, use search
      searchParam = searchQuery;
      partNoFilter = filters.partNo || "";
    }

    const params = {
      ...(categoryId ? { categoryId } : { categoryFilter }),
      manufacturerFilter,
      ...(searchParam && { search: searchParam }),
      ...(partNoFilter && { partNoFilter }),
    };

    try {
      setMaterialState((prev) => ({ ...prev, isLoadingItems: true }));

      const inventoryResult = await dispatch(getInventoryLists(params));

      const items = inventoryResult?.category || [];

      setMaterialState((prev) => ({
        ...prev,
        overlayItems: items,
        isLoadingItems: false,
      }));
    } catch (error) {
      console.error("Error fetching items for overlay:", error);
      setMaterialState((prev) => ({
        ...prev,
        overlayItems: [],
        isLoadingItems: false,
      }));
    }
  };

  /**
   * Handles adding new item when no search results found in overlay
   *
   * @description Closes the overlay and opens the Add Item Dialog with the current search query
   * pre-filled. Allows users to create new inventory items when their search doesn't match
   * existing items.
   *
   * @returns {void} Closes overlay and opens Add Item Dialog
   *
   * @dependencies
   * - materialState.currentOverlayTmpId: Current temporary row ID
   * - AddItemDialogHandler(): Function to open the add item dialog
   *
   * @example
   * // User clicks "Add New Item" button in overlay when no results found
   * onAddNewItemFromOverlay();
   */
  const onAddNewItemFromOverlay = () => {
    const { currentOverlayTmpId } = materialState;
    const searchQuery =
      idx(materialState, (_) => _.filterValue[currentOverlayTmpId]?.item) || "";

    closeItemOverlay();
    AddItemDialogHandler(currentOverlayTmpId);
  };

  return (
    <>
      <AddItemDialog
        open={materialState.addItemDialogState}
        submitHandler={(res) => AddItemDialogClose(res)}
        dialogClose={() =>
          setMaterialState((prev) => ({ ...prev, addItemDialogState: false }))
        }
        categoryId={
          idx(
            materialState,
            (_) => _.filterValue[materialState.tmpId].catalogid
          ) || ""
        }
        itemName={
          idx(materialState, (_) => _.filterValue[materialState.tmpId].item) ||
          ""
        }
        manufacturer={
          idx(
            materialState,
            (_) => _.filterValue[materialState.tmpId].manufacturer
          ) || ""
        }
        partNo={
          idx(
            materialState,
            (_) => _.filterValue[materialState.tmpId].partNo
          ) || ""
        }
        unitCost={
          idx(
            materialState,
            (_) => _.filterValue[materialState.tmpId].costPerItem
          ) || ""
        }
        writePermission={wCatalogPermission}
      />

      {[...material, ...newMaterial].length > 0 && (
        <div className="mb-2">
          <div className="border border-bottom-0 d-flex px-2 py-1 gap-3">
            <h2 className="py-1 tbl-heading">Material List</h2>
          </div>

          <div className="material-list-block" data-cy="material-list">
            <div className="table-responsive">
              <Table bordered className="table-create table-material tbl-fixed">
                <thead>
                  <tr>
                    <th>Catalog</th>
                    <th className="manufacturer-width">Category</th>
                    <th className="partno-width">Part No</th>
                    <th className="item-width">Item Description</th>
                    <th className="text-right qty-width">UoM</th>
                    <th className="cost-width text-right">Cost($)</th>
                    <th className="cost-width text-right">Sale Amount($)</th>
                    <th className="qty-width text-right">Quantity</th>
                    <th className="text-right total-width">Total ($)</th>
                    <th className="text-right action-width">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {material.map((_material, materialIndex) => {
                    if (!_material) return;

                    // Try to get category name from stored categoryName first, then fallback to lookup
                    let categoryName = _material.categoryName;
                    if (!categoryName) {
                      let category = getCategoriesList().find(
                        (item) => item.value === _material.categoryId
                      );
                      categoryName = category?.name;
                    }

                    return (
                      <tr key={materialIndex}>
                        <td>
                          <p
                            title={
                              isInlineItem(_material)
                                ? INLINE_CATALOG_DISPLAY_NAME
                                : categoryName
                            }
                            className="text-truncate m-0 table-text-sm"
                          >
                            {isInlineItem(_material)
                              ? INLINE_CATALOG_DISPLAY_NAME
                              : categoryName}
                          </p>
                        </td>
                        <td>
                          <p
                            title={_material.manufacturer}
                            className="text-truncate m-0 table-text-sm"
                          >
                            {_material.manufacturer}
                          </p>
                        </td>
                        <td>
                          <p
                            title={_material.partNo}
                            className="text-truncate m-0 table-text-sm"
                          >
                            {_material.partNo}
                          </p>
                        </td>
                        <td>
                          <p
                            title={idx(_material, (_) => _.name.value) || ""}
                            className="text-truncate m-0 table-text-sm"
                          >
                            {idx(_material, (_) => _.name.value) || ""}
                          </p>
                        </td>
                        <td className="text-right">
                          <p
                            title={idx(_material, (_) => _.unit.value) || ""}
                            className="text-truncate m-0 table-text-sm"
                          >
                            {idx(_material, (_) => _.unit.value) || ""}
                          </p>
                        </td>
                        <td>
                          <NumberInput
                            className={"input-block-sm"}
                            {..._material.costPerItem}
                            onChange={(e) =>
                              itemChangeHandler(e, materialIndex)
                            }
                            onBlur={(e) => itemChangeHandler(e, materialIndex)}
                            customInputClass="text-right"
                          />
                        </td>
                        <td>
                          <LockInput
                            formatDecimals
                            className={"input-block-sm"}
                            lock={
                              <span className="lock-icon">
                                {idx(
                                  _material,
                                  (_) => _.saleAmountLock.value
                                ) && (
                                    <LockIcon
                                      onClick={(e) => {
                                        if (!isDisabled)
                                          itemChangeHandler(
                                            "saleAmountLock",
                                            materialIndex
                                          );
                                      }}
                                    />
                                  )}
                                {!idx(
                                  _material,
                                  (_) => _.saleAmountLock.value
                                ) && (
                                    <LockOpenIcon
                                      onClick={(e) => {
                                        if (!isDisabled)
                                          itemChangeHandler(
                                            "saleAmountLock",
                                            materialIndex
                                          );
                                      }}
                                    />
                                  )}
                              </span>
                            }
                            {..._material.saleAmount}
                            disabled={
                              isDisabled ||
                                idx(_material, (_) => _.saleAmountLock.value)
                                ? true
                                : false
                            }
                            onChange={(e) =>
                              itemChangeHandler(e, materialIndex)
                            }
                            onBlur={(e) => itemChangeHandler(e, materialIndex)}
                          />
                        </td>
                        {/** Quantity field */}
                        <td>
                          <NumberInput
                            className={"input-block-sm"}
                            {..._material.qty}
                            disabled={isDisabled}
                            onChange={(e) =>
                              itemChangeHandler(e, materialIndex)
                            }
                            onBlur={(e) => itemChangeHandler(e, materialIndex)}
                            allowNegative={isVariation}
                            {...(!isVariation && { min: 0 })}
                            customInputClass="text-right"
                          />
                        </td>
                        <td className="text-right table-text-sm">
                          {toUSD(_material.total.value)}
                        </td>
                        <td className="text-right">
                          {!isDisabled && (
                            <DeleteIcon
                              className="deleteicon"
                              onClick={() => removeItem(materialIndex)}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {newMaterial.map((_material) => (
                    <tr key={_material.id}>
                      <td>
                        <div className="input-block-sm autocomplete-block autocomplete-block-ch">
                          <Autocomplete
                            onChange={(event, newValue) => {
                              if (typeof newValue === "string") {
                              } else if (newValue && newValue.inputValue) {
                              } else if (newValue) {
                                addFilter(
                                  newValue.key,
                                  "catalog",
                                  _material.id
                                );
                                addFilter(
                                  newValue.value,
                                  "catalogid",
                                  _material.id
                                );
                              } else {
                                addFilter("", "catalog", _material.id);
                              }
                            }}
                            filterOptions={(options, params) =>
                              filter(options, params)
                            }
                            selectOnFocus
                            handleHomeEndKeys
                            clearOnEscape={false}
                            clearOnBlur={false}
                            clearText={true}
                            options={getCategoriesList()}
                            getOptionLabel={(option) =>
                              typeof option === "string" ? option : option.key
                            }
                            // renderOption={(props, option) => <li {...props}>{option.key}</li>}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                label="Catalog"
                              />
                            )}
                            disablePortal={true}
                            id="combo-box-demo"
                            onInputChange={(event, newValue) => {
                              addFilter(
                                newValue.key || newValue,
                                "catalog",
                                _material.id
                              );
                              addFilter(
                                newValue.value,
                                "catalogid",
                                _material.id
                              );
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="input-block-sm autocomplete-block autocomplete-block-ch">
                          <Autocomplete
                            onChange={(event, newValue) => {
                              if (typeof newValue === "string") {
                              } else if (newValue && newValue.inputValue) {
                              } else if (newValue) {
                                addFilter(
                                  newValue.value,
                                  "manufacturer",
                                  _material.id
                                );
                              } else {
                                addFilter("", "manufacturer", _material.id);
                              }
                            }}
                            // filterOptions={(options, params) => filter(options, params)}
                            selectOnFocus
                            handleHomeEndKeys
                            options={
                              idx(
                                materialState,
                                (_) =>
                                  _.filterValue[_material.id]
                                    .manufacturerOptions
                              ) ||
                              manufacturerDropDown(_material.id) ||
                              []
                            }
                            // options={manufacturerDropDown(_material.id)}
                            getOptionLabel={(option) =>
                              typeof option === "string" ? option : option.key
                            }
                            // renderOption={(props, option) => <li {...props}>{option.key}</li>}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                label="Manufacturer"
                              />
                            )}
                            disablePortal={true}
                            clearOnEscape={false}
                            clearOnBlur={false}
                            clearText={true}
                            id="combo-box-demo"
                            onInputChange={(event, newValue) => {
                              addFilter(newValue, "manufacturer", _material.id);
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="input-block-sm autocomplete-block autocomplete-block-ch">
                          <CustomInput
                            className="input-block-sm"
                            fullWidth
                            value={
                              idx(
                                materialState,
                                (_) => _.filterValue[_material.id].partNo
                              ) || ""
                            }
                            inputProps={{
                              ref: (el) => {
                                if (el) {
                                  inputRefs.current[`partNo-${_material.id}`] =
                                    el;
                                }
                              },
                            }}
                            onChange={(e) => {
                              const query = e.target.value;
                              const targetElement = e.target; // Capture target element
                              addFilter(query, "partNo", _material.id);

                              if (query && query.length > 0) {
                                // Clear existing timer
                                clearTimeout(searchTimerRef.current);

                                // Set new timer to open overlay after delay
                                searchTimerRef.current = setTimeout(() => {
                                  const inputEl =
                                    inputRefs.current[`partNo-${_material.id}`];
                                  if (inputEl) {
                                    openItemOverlay(
                                      _material.id,
                                      inputEl,
                                      query,
                                      true
                                    ); // true indicates partNo search
                                  } else {
                                    openItemOverlay(
                                      _material.id,
                                      targetElement,
                                      query,
                                      true
                                    );
                                  }
                                }, 500);
                              } else {
                                closeItemOverlay();
                              }
                            }}
                          />
                          {/* Show "Add new item" only when no overlay is open and there's search text */}
                          {!materialState.overlayOpen &&
                            materialState.overlayItems.length === 0 &&
                            idx(
                              materialState,
                              (_) => _.filterValue[_material.id].partNo
                            ) &&
                            idx(
                              materialState,
                              (_) => _.filterValue[_material.id].partNo
                            ) > 0 && (
                              <div
                                className="position-absolute bg-white border rounded p-1 mt-1"
                                style={{ fontSize: "12px", zIndex: 1000 }}
                              >
                                <span
                                  style={{
                                    cursor: "pointer",
                                    color: "#007bff",
                                  }}
                                  onClick={() =>
                                    AddItemDialogHandler(_material.id)
                                  }
                                >
                                  Add item with Part No "
                                  {idx(
                                    materialState,
                                    (_) => _.filterValue[_material.id].partNo
                                  )}
                                  "
                                </span>
                              </div>
                            )}
                        </div>
                      </td>
                      <td className="item-width">
                        <div className="input-block-sm autocomplete-block autocomplete-block-ch">
                          <CustomInput
                            data-cy="material-item-search"
                            className="input-block-sm"
                            fullWidth
                            value={
                              idx(
                                materialState,
                                (_) => _.filterValue[_material.id].item
                              ) || ""
                            }
                            inputProps={{
                              ref: (el) => {
                                if (el) {
                                  inputRefs.current[`item-${_material.id}`] =
                                    el;
                                }
                              },
                            }}
                            onChange={(e) => {
                              const query = e.target.value;
                              const targetElement = e.target; // Capture target element
                              addFilter(query, "item", _material.id, true);

                              if (query && query.length > 0) {
                                // Clear existing timer
                                clearTimeout(searchTimerRef.current);

                                // Set new timer to open overlay after delay
                                searchTimerRef.current = setTimeout(() => {
                                  const inputEl =
                                    inputRefs.current[`item-${_material.id}`];
                                  if (inputEl) {
                                    openItemOverlay(
                                      _material.id,
                                      inputEl,
                                      query
                                    );
                                  } else {
                                    openItemOverlay(
                                      _material.id,
                                      targetElement,
                                      query
                                    );
                                  }
                                }, 500);
                              } else {
                                closeItemOverlay();
                              }
                            }}
                          />
                          {/* Show "Add new item" only when no overlay is open and there's search text */}
                          {!materialState.overlayOpen &&
                            materialState.overlayItems.length === 0 &&
                            idx(
                              materialState,
                              (_) => _.filterValue[_material.id].item
                            ) &&
                            idx(
                              materialState,
                              (_) => _.filterValue[_material.id].item
                            ) > 0 && (
                              <div
                                className="position-absolute bg-white border rounded p-1 mt-1"
                                style={{ fontSize: "12px", zIndex: 1000 }}
                              >
                                <span
                                  style={{
                                    cursor: "pointer",
                                    color: "#007bff",
                                  }}
                                  onClick={() =>
                                    AddItemDialogHandler(_material.id)
                                  }
                                >
                                  Add "
                                  {idx(
                                    materialState,
                                    (_) => _.filterValue[_material.id].item
                                  )}
                                  " as new item
                                </span>
                              </div>
                            )}
                        </div>
                      </td>
                      <td>
                        <div className="form-group">
                          <SelectInput
                            className="input-block-sm"
                            name="unit"
                            value={idx(
                              materialState,
                              (_) => _.filterValue[_material.id].unit
                            ) || ""}
                            items={materialState.item?.unit?.items || []}
                            placeholder="Unit"
                            customClassName="w-100"
                            onChange={(e) => {
                              addFilter(e.target.value, "unit", _material.id);
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <NumberInput
                          type="number"
                          className="input-block-sm"
                          formatDecimals={true}
                          customInputClass="text-right"
                          value={idx(
                            materialState,
                            (_) => _.filterValue[_material.id].costPerItem
                          ) || ""}
                          onChange={(e) => {
                            if (e.target.value >= 0)
                              addFilter(
                                e.target.value,
                                "costPerItem",
                                _material.id
                              );
                          }}
                        />
                      </td>
                      <td>
                        <LockInput
                          lock={
                            <span className="lock-icon">
                              {getLockStatus(_material.id) ? (
                                <LockIcon
                                  onClick={() =>
                                    lockMaterialIconChange(_material.id)
                                  }
                                />
                              ) : (
                                <LockOpenIcon
                                  onClick={() =>
                                    lockMaterialIconChange(_material.id)
                                  }
                                />
                              )}
                            </span>
                          }
                          type="number"
                          className="input-block-sm"
                          value={idx(
                            materialState,
                            (_) => _.filterValue[_material.id].saleAmount
                          )}
                          formatDecimals={true}
                          disabled={getLockStatus(_material.id)}
                          onChange={(e) => {
                            if (e.target.value >= 0)
                              addFilter(
                                e.target.value,
                                "saleAmount",
                                _material.id
                              );
                          }}
                          min={0}
                        />
                      </td>
                      <td className="text-right table-text-sm">
                        <NumberInput
                          type="number"
                          className="input-block-sm"
                          name="qty"
                          value={idx(
                            materialState,
                            (_) => _.filterValue[_material.id].qty
                          ) || ""}
                          onChange={(e) => {
                            // Allow negative quantities only for variations
                            if (isVariation || e.target.value >= 0)
                              addFilter(e.target.value, "qty", _material.id);
                          }}
                        />
                      </td>
                      <td className="text-right table-text-sm">
                        {toUSD(
                          idx(
                            materialState,
                            (_) => _.filterValue[_material.id].totalValue
                          )
                        )}
                      </td>
                      <td className="text-right">
                        {!isDisabled && (
                          <>
                            <Tooltip
                              title="Add as Inline Item"
                              placement="top"
                              arrow
                              followCursor={true}
                              PopperProps={{ style: { zIndex: 9999 } }}
                            >
                              <AddIcon
                                className="add-icon me-1"
                                onClick={() => addInlineItem(_material.id)}
                              />
                            </Tooltip>

                            <DeleteIcon
                              className="deleteicon"
                              onClick={() => handleRemoveMaterial(_material.id)}
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  ))}

                  <tr>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td className="text-right fs-6 py-1">
                      <span>{toUSD(idx(total, (_) => _.material.sale))}</span>
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      )}

      <ProfitMarginLock
        open={materialState.profitMarginDialogState}
        lockIconChange={() => setConfirmed()}
        dialogClose={profitMarginDialogHandler}
        moduleName="Task"
        modalHeading={"Sale Amount Lock changes"}
        text={"sale Amount"}
        message={
          "Are you sure you want to change the Project profit margin? - Any items using default profit margins will also be updated to this value"
        }
      />

      <ItemSelectionOverlay
        open={materialState.overlayOpen}
        anchorEl={materialState.overlayAnchorEl}
        items={materialState.overlayItems}
        selectedItemIds={materialState.selectedItemIds}
        isLoading={materialState.isLoadingItems}
        onClose={closeItemOverlay}
        onSelectItem={handleItemSelection}
        onAddItems={addSelectedItemsToMaterial}
        onAddNewItem={onAddNewItemFromOverlay}
        initialSearchValue={
          materialState.isPartNoSearch
            ? idx(
              materialState,
              (_) => _.filterValue[materialState.currentOverlayTmpId].partNo
            ) || ""
            : idx(
              materialState,
              (_) => _.filterValue[materialState.currentOverlayTmpId].item
            ) || ""
        }
        isPartNoSearch={materialState.isPartNoSearch}
      />
    </>
  );
}
